import Typed from 'typed.js';
import database from '../../config/game.json';

export function getGameData() {
  return database;
}

export function getChapter(num = 1) {
  return database.chapters[num] || null;
}

export function getPlayerState() {
  return { ...database.player };
}

export function getRoom(chapter, id) {
  if (!chapter || !chapter.rooms) return null;
  return chapter.rooms[id] || null;
}

export function firstRoom(chapter) {
  return getRoom(chapter, '1');
}

export function moveCommands() {
  return ['north', 'south', 'east', 'west', 'up', 'down', 'northeast', 'northwest', 'southeast', 'southwest'];
}

export function combatCommands() {
  return ['attack', 'defend', 'flee'];
}

export function inventoryCommands() {
  return ['inventory', 'inv'];
}

export function helpCommands() {
  return ['help'];
}

export function lookupCommands() {
  return ['search', 'look', 'examine'];
}

export function getCommands() {
  return ['get', 'gather', 'take', 'pick'];
}

export function useCommands() {
  return ['use', 'drink', 'eat', 'apply'];
}

export function unlockCommands() {
  return ['unlock', 'open', 'insert'];
}

export function saveCommands() {
  return ['save'];
}

export function loadCommands() {
  return ['load'];
}

export function compassCommands() {
  return ['compass', 'history'];
}

export function mapCommands() {
  return ['map'];
}

export function prefix(msg = '') {
  return "# " + msg;
}

export function getItemData(chapter, itemName) {
  // Prima cerca nel capitolo corrente
  if (chapter.items[itemName]) {
    return chapter.items[itemName];
  }
  
  // Se non trovato, cerca in tutti i capitoli (per item raccolti in capitoli precedenti)
  const allChapters = getGameData().chapters;
  for (const chNum in allChapters) {
    const ch = allChapters[chNum];
    if (ch.items[itemName]) {
      return ch.items[itemName];
    }
  }
  
  return null;
}

export function commandNotAllowedMsg() {
  const msgs = [
    "You cannot do that",
    "That doesn't work",
    "Invalid command"
  ];
  return msgs[Math.floor(Math.random() * msgs.length)];
}

export function lookupResultMsg(item) {
  const msgs = [
    `You found a ${item}!`,
    `There's a ${item} here.`,
    `You spot a ${item}.`
  ];
  return msgs[Math.floor(Math.random() * msgs.length)];
}

export function itemGotMsg(item) {
  const msgs = [
    `You took the ${item}.`,
    `${item} added to inventory.`,
    `You picked up the ${item}.`
  ];
  return msgs[Math.floor(Math.random() * msgs.length)];
}

export function helpMsg() {
  return `<strong>KROZ - Text Adventure</strong><br/><br/>
${prefix()} <strong>Movement:</strong> north, south, east, west, up, down, northeast, etc.<br/>
${prefix()} <strong>Search:</strong> search, look, examine [object]<br/>
${prefix()} <strong>Items:</strong> get/take [item], use [item]<br/>
${prefix()} <strong>Combat:</strong> attack [weapon], defend, flee<br/>
${prefix()} <strong>Inventory:</strong> inventory or inv<br/>
${prefix()} <strong>Unlock:</strong> unlock [direction] with [item]<br/>
${prefix()} <strong>Navigation:</strong> compass (last 3 moves), map (current area)<br/>
${prefix()} <strong>Save/Load:</strong> save, load<br/>
${prefix()} <strong>Help:</strong> help<br/><br/>
${prefix()} <em>Tip: Some doors need specific keys or combinations. Search carefully!</em>`;
}

export function inventoryMsg(chapter, inventory, player) {
  let msg = `<strong>INVENTORY</strong><br/>HP: ${player.hp}/${player.max_hp} | Status: ${player.status}`;
  if (inventory.length === 0) {
    msg += "<br/><br/>Empty";
  } else {
    msg += "<br/><br/>";
    const weapons = inventory.filter(i => getItemData(chapter, i)?.type === 'weapon');
    const healing = inventory.filter(i => getItemData(chapter, i)?.type === 'healing');
    const keys = inventory.filter(i => ['key', 'mechanism_part'].includes(getItemData(chapter, i)?.type));
    const other = inventory.filter(i => {
      const itemData = getItemData(chapter, i);
      return itemData && !['weapon', 'healing', 'key', 'mechanism_part'].includes(itemData.type);
    });
    
    if (weapons.length > 0) {
      msg += "<strong>Weapons:</strong><br/>";
      weapons.forEach(w => {
        const data = getItemData(chapter, w);
        if (data) {
          msg += `${prefix()} ${data.name} (${data.damage} dmg)<br/>`;
        }
      });
    }
    if (healing.length > 0) {
      msg += "<strong>Healing:</strong><br/>";
      healing.forEach(h => {
        const data = getItemData(chapter, h);
        if (data) {
          msg += `${prefix()} ${data.name} (+${data.heal} hp)<br/>`;
        }
      });
    }
    if (keys.length > 0) {
      msg += "<strong>Keys/Parts:</strong><br/>";
      keys.forEach(k => {
        const data = getItemData(chapter, k);
        if (data) {
          msg += `${prefix()} ${data.name}<br/>`;
        }
      });
    }
    if (other.length > 0) {
      msg += "<strong>Other:</strong><br/>";
      other.forEach(o => {
        const data = getItemData(chapter, o);
        if (data) {
          msg += `${prefix()} ${data.name}<br/>`;
        }
      });
    }
  }
  return msg;
}

export function compassMsg(history) {
  if (history.length === 0) return "No movement history yet.";
  let msg = "<strong>COMPASS - Last 3 Moves:</strong><br/>";
  const last3 = history.slice(-3);
  last3.forEach((h, i) => {
    msg += `${prefix()} ${i + 1}. ${h.direction} → ${h.room}<br/>`;
  });
  return msg;
}

export function generateMap(currentRoom, chapter) {
  const moves = currentRoom.moves || {};
  
  // Determina se è una stanza di transizione (pochi elementi, solo passaggio)
  const isTransition = !currentRoom.searchable || Object.keys(currentRoom.searchable).length === 0;
  const hasItems = currentRoom.items && currentRoom.items.length > 0;
  const hasEnemies = currentRoom.enemies && currentRoom.enemies.length > 0;
  
  let map = "<strong>MAP:</strong><br/><pre style='line-height: 1.2;'>";
  
  if (isTransition && !hasItems && !hasEnemies) {
    // Stanza di transizione - mostra stanze limitrofe
    map += generateTransitionMap(currentRoom, chapter, moves);
  } else {
    // Stanza vera - mostra dettagli stanza corrente
    map += generateDetailedRoom(currentRoom, moves);
  }
  
  map += "</pre>";
  return map;
}

function generateDetailedRoom(room, moves) {
  let map = "";
  const width = 38;
  
  // Top border con uscite
  map += "╔";
  if (moves.north || moves.up) {
    map += "═".repeat(17) + "[N]" + "═".repeat(17);
  } else {
    map += "═".repeat(width);
  }
  map += "╗\n";
  
  // Room name
  const name = room.name.substring(0, width - 2);
  const padding = Math.floor((width - name.length) / 2);
  map += "║" + " ".repeat(padding) + name + " ".repeat(width - padding - name.length) + "║\n";
  map += "║" + "─".repeat(width) + "║\n";
  
  // West/East exits
  const westExit = moves.west ? "[W]" : "║  ";
  const eastExit = moves.east ? "[E]" : "  ║";
  
  // Room contents - solo descrizione generica
  const contents = [];
  
  // Analizza descrizione per oggetti
  const desc = room.description.toLowerCase();
  if (desc.includes('table') || desc.includes('desk')) contents.push("  🪑 Furniture");
  if (desc.includes('fireplace')) contents.push("  🔥 Fireplace");
  if (desc.includes('bed')) contents.push("  🛏️  Bed");
  if (desc.includes('window')) contents.push("  🪟 Window");
  if (desc.includes('door')) contents.push("  🚪 Door");
  if (desc.includes('book') || desc.includes('shelves')) contents.push("  📚 Books");
  if (desc.includes('altar')) contents.push("  ⛪ Altar");
  if (desc.includes('tree') || desc.includes('forest')) contents.push("  🌲 Trees");
  if (desc.includes('water') || desc.includes('well')) contents.push("  💧 Water");
  if (desc.includes('stone') || desc.includes('rock')) contents.push("  🪨 Stones");
  
  // YOU marker sempre presente
  contents.push("  👤 YOU");
  
  // Draw contents
  const maxLines = 6;
  for (let i = 0; i < maxLines; i++) {
    if (i < contents.length) {
      const line = contents[i].substring(0, width - 4);
      map += westExit + line + " ".repeat(width - 4 - line.length) + eastExit + "\n";
    } else {
      map += westExit + " ".repeat(width - 4) + eastExit + "\n";
    }
  }
  
  // Bottom border con uscite
  map += "╚";
  if (moves.south || moves.down) {
    map += "═".repeat(17) + "[S]" + "═".repeat(17);
  } else {
    map += "═".repeat(width);
  }
  map += "╝\n";
  
  // Available exits
  const exits = Object.keys(moves).filter(d => !d.includes('_alt'));
  if (exits.length > 0) {
    map += "\nExits: " + exits.join(", ");
  }
  
  return map;
}

function generateTransitionMap(currentRoom, chapter, moves) {
  let map = "";
  
  // Get adjacent rooms
  const north = moves.north ? getRoom(chapter, moves.north) : null;
  const south = moves.south ? getRoom(chapter, moves.south) : null;
  const east = moves.east ? getRoom(chapter, moves.east) : null;
  const west = moves.west ? getRoom(chapter, moves.west) : null;
  
  // Top room (north)
  if (north && !north.id.startsWith('chapter_')) {
    map += "     ┌─────────────┐\n";
    map += `     │ ${north.name.substring(0, 11).padEnd(11)} │\n`;
    map += "     └──────┬──────┘\n";
    map += "            │\n";
  } else {
    map += "\n\n\n\n";
  }
  
  // Middle row (west - current - east)
  const leftRoom = west && !west.id.startsWith('chapter_') ? west.name.substring(0, 8).padEnd(8) : "        ";
  const rightRoom = east && !east.id.startsWith('chapter_') ? east.name.substring(0, 8).padEnd(8) : "        ";
  
  if (west && !west.id.startsWith('chapter_')) {
    map += "┌────────┐";
  } else {
    map += "          ";
  }
  map += "  ╔═══════╗  ";
  if (east && !east.id.startsWith('chapter_')) {
    map += "┌────────┐\n";
  } else {
    map += "\n";
  }
  
  if (west && !west.id.startsWith('chapter_')) {
    map += `│${leftRoom}├──`;
  } else {
    map += "             ";
  }
  map += "║  YOU  ║";
  if (east && !east.id.startsWith('chapter_')) {
    map += `──┤${rightRoom}│\n`;
  } else {
    map += "\n";
  }
  
  if (west && !west.id.startsWith('chapter_')) {
    map += "└────────┘";
  } else {
    map += "          ";
  }
  map += "  ╚═══════╝  ";
  if (east && !east.id.startsWith('chapter_')) {
    map += "└────────┘\n";
  } else {
    map += "\n";
  }
  
  // Bottom room (south)
  if (south && !south.id.startsWith('chapter_')) {
    map += "            │\n";
    map += "     ┌──────┴──────┐\n";
    map += `     │ ${south.name.substring(0, 11).padEnd(11)} │\n`;
    map += "     └─────────────┘\n";
  }
  
  // Current room name
  map += `\n📍 ${currentRoom.name}`;
  
  return map;
}

export function prepareTypedBox(ref, outputs = [''], startDelay = 0, typeSpeed = 10) {
  return new Typed(ref, {
    strings: outputs,
    typeSpeed: typeSpeed,
    startDelay: startDelay,
    contentType: 'html',
    showCursor: false,
  });
}

export function updateTypedBox(box, outputs = [''], startDelay = 0, typeSpeed = 10) {
  if (!box) return;
  box.strings = outputs;
  box.typeSpeed = typeSpeed;
  box.startDelay = startDelay;
  box.reset(true);
}

export function saveGame(state) {
  try {
    localStorage.setItem('kroz_save', JSON.stringify(state));
    return true;
  } catch (e) {
    return false;
  }
}

export function loadGame() {
  try {
    const data = localStorage.getItem('kroz_save');
    return data ? JSON.parse(data) : null;
  } catch (e) {
    return null;
  }
}
